import React from 'react';

import { TableProps } from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import Link from '@amzn/awsui-components-console/link';
import SpaceBetween from '@amzn/awsui-components-console/space-between';

import { StringGroupItem } from 'src/generated/graphql';
import { formatEpochMillisDate } from './utils';
import ExpandableCell from 'src/features/namespace/components/ExpandableCell';
import { useNavigate } from 'react-router';

interface StringGroupLinkProps {
  namespaceId?: string;
  stringGroupId?: string;
}

const StringGroupLink = ({
  namespaceId,
  stringGroupId,
}: StringGroupLinkProps) => {
  const navigate = useNavigate();
  const href = `/${namespaceId}/${stringGroupId}`;
  return (
    <Link
      variant="secondary"
      href={href}
      onFollow={(e) => {
        e.preventDefault();
        navigate(href);
      }}
    >
      {stringGroupId}
    </Link>
  );
};

export const getStringGroupColumns = (
  namespaceId: string,
): TableProps.ColumnDefinition<StringGroupItem>[] => [
  {
    id: 'stringGroupId',
    header: 'Name',
    sortingField: 'stringGroupId',
    minWidth: 140,
    cell: (item) => (
      <StringGroupLink
        namespaceId={namespaceId}
        stringGroupId={item.stringGroupId}
      />
    ),
  },
  {
    id: 'clientId',
    header: 'Client ID',
    minWidth: 80,
    cell: (item) => item.clientId ?? '—',
  },
  {
    id: 'teamName',
    header: 'Team Name',
    minWidth: 80,
    sortingField: 'teamName',
    cell: (item) => item.teamName ?? '—',
  },
  {
    id: 'cxFamilyId',
    header: 'CX Family',
    minWidth: 90,
    sortingComparator: (a, b) =>
      (a.latestVersion?.cxFamilyId ?? '').localeCompare(
        b.latestVersion?.cxFamilyId ?? '',
      ),
    cell: (item) => item.latestVersion?.cxFamilyId ?? '—',
  },
  {
    id: 'cxIds',
    header: 'CX IDs',
    minWidth: 100,
    cell: (item) => (
      <ExpandableCell items={item.latestVersion?.cxIds} maxVisible={3} />
    ),
  },
  {
    id: 'sourceLocale',
    header: 'Source locale',
    width: 100,
    cell: (item) => item.latestVersion?.sourceLocale ?? '—',
  },
  {
    id: 'stringCount',
    header: 'String Count',
    width: 100,
    sortingComparator: (a, b) =>
      (a.latestVersion?.stringCount ?? 0) - (b.latestVersion?.stringCount ?? 0),
    cell: (item) => item.latestVersion?.stringCount ?? '—',
  },
  {
    id: 'createdAt',
    header: 'Created On',
    minWidth: 120,
    sortingComparator: (a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0),
    cell: (item) => (
      <SpaceBetween direction="horizontal" size="xs">
        <Box variant="span">{formatEpochMillisDate(item?.createdAt)}</Box>
        {item?.createdBy && (
          <Box variant="span" color="text-body-secondary">
            by {item.createdBy}
          </Box>
        )}
      </SpaceBetween>
    ),
  },
  {
    id: 'updatedAt',
    header: 'Updated On',
    minWidth: 120,
    sortingComparator: (a, b) =>
      (a.latestVersion?.updatedAt ?? 0) - (b.latestVersion?.updatedAt ?? 0),
    cell: (item) => (
      <SpaceBetween direction="horizontal" size="xs">
        <Box variant="span">
          {formatEpochMillisDate(item.latestVersion?.updatedAt)}
        </Box>
        {item.latestVersion?.updatedBy && (
          <Box variant="span" color="text-body-secondary">
            by {item.latestVersion.updatedBy}
          </Box>
        )}
      </SpaceBetween>
    ),
  },
];
